import { Device } from '@twilio/voice-sdk';
import axios from 'axios';

// Estado global del cliente
let device = null;
let conexionActual = null;
let estadoDevice = 'disconnected'; // 'disconnected', 'connecting', 'ready', 'error'

// Configuración
const CONFIG = {
  tokenUrl: 'https://academicokpi.club/kpi/twilio.token',
  debug: true,
  timeout: 15000, // 15 segundos timeout (aumentado)
  reintentos: 3
};

// Eventos personalizados para el componente Vue
const eventBus = {
  callbacks: {},
  on(evento, callback) {
    if (!this.callbacks[evento]) {
      this.callbacks[evento] = [];
    }
    this.callbacks[evento].push(callback);
  },
  off(evento, callback) {
    if (this.callbacks[evento]) {
      this.callbacks[evento] = this.callbacks[evento].filter(cb => cb !== callback);
    }
  },
  emit(evento, data) {
    if (this.callbacks[evento]) {
      this.callbacks[evento].forEach(callback => callback(data));
    }
  }
};

/**
 * Inicializar el cliente Twilio
 * @returns {Promise} Promesa que se resuelve cuando el device está listo
 */
export async function inicializarTwilio() {
  try {
    estadoDevice = 'connecting';
    eventBus.emit('estadoCambiado', { estado: 'connecting', mensaje: 'Conectando...' });

    // Si ya existe un device, limpiarlo
    if (device) {
      await limpiarDevice();
    }

    console.log('🔧 Inicializando Twilio Device...');

    // Obtener token con timeout y reintentos
    const token = await obtenerTokenConReintentos();
    console.log('🔑 Token obtenido correctamente');

    // Crear nuevo device con configuración simplificada
    device = new Device(token, {
      debug: CONFIG.debug,
      allowIncomingWhileBusy: false,
      codecPreferences: ['opus', 'pcmu'],
      identity: 'usuario-prueba'
    });

    console.log('✅ Device creado:', device);
    console.log('📊 Estado inicial del device:', device.state);

    // Configurar eventos del device ANTES de cualquier otra operación
    configurarEventosDevice();

    // Reanudar AudioContext si está suspendido
    await reanudarAudioContext();

    // IMPORTANTE: Registrar el device manualmente para recibir llamadas
    console.log('📡 Registrando device...');
    await device.register();
    console.log('✅ Device registrado exitosamente');

    // Esperar a que el device esté listo
    await esperarDeviceListo();
    
    estadoDevice = 'ready';
    eventBus.emit('estadoCambiado', { estado: 'ready', mensaje: 'Listo para llamar' });
    
    console.log('🎉 Device completamente inicializado y listo');
    return device;
    
  } catch (error) {
    console.error('❌ Error al inicializar Twilio:', error);
    estadoDevice = 'error';
    eventBus.emit('estadoCambiado', { estado: 'error', mensaje: error.message });
    throw new Error(`Error al inicializar Twilio: ${error.message}`);
  }
}

/**
 * Realizar una llamada
 * @param {string} numeroDestino - Número a llamar (formato: +521XXXXXXXXXX)
 * @returns {Promise} Promesa que se resuelve cuando la llamada se conecta
 */
export async function llamarANumero(numeroDestino) {
  try {
    // Validaciones
    if (!numeroDestino) {
      throw new Error('Número de destino es requerido');
    }
    
    if (!validarNumero(numeroDestino)) {
      throw new Error('Formato de número inválido');
    }
    
    if (!device || estadoDevice !== 'ready') {
      throw new Error('Twilio Device no está listo. Inicializa primero.');
    }
    
    if (conexionActual && conexionActual.status() !== 'closed') {
      throw new Error('Ya hay una llamada en curso');
    }

    // Asegurar que AudioContext esté activo
    await reanudarAudioContext();

    // Iniciar llamada
    console.log('📞 Iniciando llamada a:', numeroDestino);
    eventBus.emit('llamadaIniciando', { numero: numeroDestino });
    
    conexionActual = await device.connect({
      params: { To: numeroDestino },
      rtcConstraints: {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      }
    });

    console.log('✅ Conexión creada:', conexionActual);
    console.log('📊 Tipo de conexión:', typeof conexionActual);
    console.log('📊 ¿Tiene método "on"?:', typeof conexionActual.on);

    // Configurar eventos de la conexión solo si es válida
    if (conexionActual && typeof conexionActual.on === 'function') {
      configurarEventosConexion(conexionActual, numeroDestino);
    } else {
      console.warn('⚠️ La conexión no tiene métodos de eventos válidos');
    }
    
    return conexionActual;
    
  } catch (error) {
    console.error('❌ Error al realizar llamada:', error);
    eventBus.emit('errorLlamada', { error: error.message });
    throw error;
  }
}

/**
 * Colgar la llamada actual
 * @returns {Promise} Promesa que se resuelve cuando se cuelga
 */
export async function colgarLlamada() {
  try {
    if (!device) {
      throw new Error('Device no inicializado');
    }

    if (conexionActual && conexionActual.status() !== 'closed') {
      console.log('📴 Colgando llamada...');
      eventBus.emit('llamadaColgando', {});
      conexionActual.disconnect();
    } else {
      // Por si acaso, desconectar todas
      device.disconnectAll();
    }
    
    conexionActual = null;
    
  } catch (error) {
    console.error('❌ Error al colgar llamada:', error);
    eventBus.emit('errorLlamada', { error: error.message });
    throw error;
  }
}

/**
 * Obtener el estado actual del device
 * @returns {object} Estado actual
 */
export function obtenerEstado() {
  return {
    device: estadoDevice,
    conexion: conexionActual ? conexionActual.status() : null,
    enLlamada: conexionActual && conexionActual.status() === 'open'
  };
}

/**
 * Suscribirse a eventos del cliente
 * @param {string} evento - Nombre del evento
 * @param {Function} callback - Función callback
 */
export function suscribirseAEventos(evento, callback) {
  eventBus.on(evento, callback);
}

/**
 * Desuscribirse de eventos
 * @param {string} evento - Nombre del evento
 * @param {Function} callback - Función callback
 */
export function desuscribirseDeEventos(evento, callback) {
  eventBus.off(evento, callback);
}

/**
 * Limpiar recursos
 */
export async function limpiarRecursos() {
  try {
    console.log('🧹 Limpiando recursos...');
    
    if (conexionActual) {
      conexionActual.disconnect();
    }
    
    if (device) {
      device.disconnectAll();
      device.destroy();
    }
    
    device = null;
    conexionActual = null;
    estadoDevice = 'disconnected';
    
    console.log('✅ Recursos limpiados correctamente');
    
  } catch (error) {
    console.error('❌ Error al limpiar recursos:', error);
  }
}

// === FUNCIONES AUXILIARES ===

async function obtenerTokenConReintentos() {
  let ultimoError;
  
  for (let intento = 1; intento <= CONFIG.reintentos; intento++) {
    try {
      console.log(`🔄 Intento ${intento} de obtener token...`);
      
      const response = await axios.get(CONFIG.tokenUrl, {
        timeout: CONFIG.timeout,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.data || ( response.data && !response.data.token) ) {
        throw new Error('Token no encontrado en la respuesta');
      }

      console.log('✅ Token obtenido exitosamente');
      return response.data.token;
      
    } catch (error) {
      ultimoError = error;
      console.warn(`⚠️ Intento ${intento} fallido:`, error.message);
      
      if (intento < CONFIG.reintentos) {
        const delay = 1000 * intento; // Backoff exponencial
        console.log(`⏳ Esperando ${delay}ms antes del siguiente intento...`);
        await esperar(delay);
      }
    }
  }
  
  throw new Error(`No se pudo obtener el token después de ${CONFIG.reintentos} intentos: ${ultimoError.message}`);
}

function configurarEventosDevice() {
  console.log('🔧 Configurando eventos del device...');
  
  device.on('ready', () => {
    console.log('✅ Device listo (evento ready)');
    estadoDevice = 'ready';
    eventBus.emit('estadoCambiado', { estado: 'ready', mensaje: 'Listo para llamar' });
  });

  device.on('registered', () => {
    console.log('✅ Device registrado exitosamente');
    estadoDevice = 'ready';
    eventBus.emit('estadoCambiado', { estado: 'ready', mensaje: 'Registrado y listo' });
  });

  device.on('unregistered', () => {
    console.log('📴 Device desregistrado');
    estadoDevice = 'disconnected';
    eventBus.emit('estadoCambiado', { estado: 'disconnected', mensaje: 'Desregistrado' });
  });

  device.on('error', (error) => {
    console.error('❌ Error en Twilio Device:', error);
    console.error('📊 Estado del device en error:', device.state);
    estadoDevice = 'error';
    eventBus.emit('estadoCambiado', { estado: 'error', mensaje: error.message });
  });

  device.on('offline', () => {
    console.warn('📴 Device offline (evento offline)');
    console.warn('📊 Estado actual en offline:', device.state);
    estadoDevice = 'disconnected';
    eventBus.emit('estadoCambiado', { estado: 'disconnected', mensaje: 'Desconectado' });
  });

  device.on('incoming', (connection) => {
    console.log('📞 Llamada entrante:', connection.parameters.From);
    eventBus.emit('llamadaEntrante', { de: connection.parameters.From });
  });

  device.on('tokenWillExpire', () => {
    console.warn('⚠️ Token a punto de expirar');
    eventBus.emit('tokenExpirando', {});
  });

  device.on('registrationFailed', (error) => {
    console.error('❌ Registro fallido:', error.message);
    estadoDevice = 'error';
    eventBus.emit('estadoCambiado', { estado: 'error', mensaje: `Registro fallido: ${error.message}` });
  });

  console.log('✅ Eventos configurados correctamente');
}

function configurarEventosConexion(connection, numeroDestino) {
  console.log('🔧 Configurando eventos de conexión...');
  console.log('📊 Conexión recibida:', connection);
  console.log('📊 Tipo de conexión:', typeof connection);
  console.log('📊 ¿Tiene método "on"?:', typeof connection.on);
  
  // Verificar que la conexión sea válida antes de configurar eventos
  if (!connection || typeof connection.on !== 'function') {
    console.error('❌ La conexión no es válida o no tiene métodos de eventos');
    return;
  }
  
  connection.on('accept', () => {
    console.log('✅ Conectado con', numeroDestino);
    eventBus.emit('llamadaConectada', { numero: numeroDestino });
  });

  connection.on('disconnect', () => {
    console.log('🔚 Llamada finalizada');
    conexionActual = null;
    eventBus.emit('llamadaFinalizada', { numero: numeroDestino });
  });

  connection.on('error', (error) => {
    console.error('❌ Error en la llamada:', error);
    conexionActual = null;
    eventBus.emit('errorLlamada', { error: error.message, numero: numeroDestino });
  });

  connection.on('ringing', () => {
    console.log('📞 Timbrando...');
    eventBus.emit('llamadaTimbrando', { numero: numeroDestino });
  });

  connection.on('cancel', () => {
    console.log('🚫 Llamada cancelada');
    conexionActual = null;
    eventBus.emit('llamadaCancelada', { numero: numeroDestino });
  });

  console.log('✅ Eventos de conexión configurados correctamente');
}

async function esperarDeviceListo() {
  return new Promise((resolve, reject) => {
    console.log('⏳ Esperando que el device esté listo...');
    
    const timeout = setTimeout(() => {
      console.error('⏱️ Timeout esperando device listo (state: ' + device.state + ')');
      reject(new Error('Timeout esperando device listo'));
    }, CONFIG.timeout);

    // Si ya está listo O registrado, resolver inmediatamente
    if (device.state === 'ready' || device.state === 'registered') {
      console.log('✅ Device ya está listo/registrado');
      clearTimeout(timeout);
      resolve();
      return;
    }

    // Monitor del estado cada segundo
    const checker = setInterval(() => {
      console.log('🔄 Estado actual del device:', device.state);
    }, 1000);

    const onReady = () => {
      console.log('✅ Device listo (evento ready recibido)');
      clearTimeout(timeout);
      clearInterval(checker);
      cleanup();
      resolve();
    };

    const onRegistered = () => {
      console.log('✅ Device registrado (evento registered recibido)');
      clearTimeout(timeout);
      clearInterval(checker);
      cleanup();
      resolve();
    };

    const onError = (error) => {
      console.error('❌ Device error antes de estar listo:', error);
      clearTimeout(timeout);
      clearInterval(checker);
      cleanup();
      reject(error);
    };

    const cleanup = () => {
      device.removeListener('ready', onReady);
      device.removeListener('registered', onRegistered);
      device.removeListener('error', onError);
      device.removeListener('registrationFailed', onError);
    };

    device.once('ready', onReady);
    device.once('registered', onRegistered);
    device.once('error', onError);
    device.once('registrationFailed', onError);
  });
}

function validarNumero(numero) {
  // Validar formato +521XXXXXXXXXX
  const regex = /^\+521\d{10}$/;
  return regex.test(numero);
}

async function limpiarDevice() {
  if (device) {
    try {
      console.log('🧹 Limpiando device anterior...');
      
      if (conexionActual) {
        conexionActual.disconnect();
        conexionActual = null;
      }

      device.disconnectAll();
      device.destroy();
      
      console.log('✅ Device anterior limpiado correctamente');
    } catch (error) {
      console.error('❌ Error al limpiar Device:', error.message);
    }
  }
  device = null;
}

async function reanudarAudioContext() {
  try {
    if (device && device.audio && device.audio.context) {
      const context = device.audio.context;
      if (context.state === 'suspended') {
        console.log('🔊 Reanudando AudioContext...');
        await context.resume();
        console.log('✅ AudioContext reanudado');
      } else {
        console.log('🔊 AudioContext ya está activo:', context.state);
      }
    }
  } catch (error) {
    console.error('❌ Error al reanudar AudioContext:', error);
  }
}

function esperar(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Limpiar recursos al cerrar la ventana
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', limpiarRecursos);
}

// === FUNCIONES PARA DEBUGGING ===

/**
 * Función alternativa para hacer llamadas sin await en device.connect()
 */
export async function llamarANumeroAlternativo(numeroDestino) {
  try {
    // Validaciones
    if (!numeroDestino) {
      throw new Error('Número de destino es requerido');
    }
    
    if (!validarNumero(numeroDestino)) {
      throw new Error('Formato de número inválido');
    }
    
    if (!device || estadoDevice !== 'ready') {
      throw new Error('Twilio Device no está listo. Inicializa primero.');
    }
    
    if (conexionActual && conexionActual.status() !== 'closed') {
      throw new Error('Ya hay una llamada en curso');
    }

    // Asegurar que AudioContext esté activo
    await reanudarAudioContext();

    // Iniciar llamada SIN await
    console.log('📞 Iniciando llamada a:', numeroDestino);
    eventBus.emit('llamadaIniciando', { numero: numeroDestino });
    
    conexionActual = device.connect({
      params: { To: numeroDestino },
      rtcConstraints: {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      }
    });

    console.log('✅ Conexión creada (sin await):', conexionActual);
    console.log('📊 Tipo de conexión:', typeof conexionActual);
    console.log('📊 ¿Tiene método "on"?:', typeof conexionActual.on);

    // Configurar eventos inmediatamente
    configurarEventosConexion(conexionActual, numeroDestino);
    
    return conexionActual;
    
  } catch (error) {
    console.error('❌ Error al realizar llamada:', error);
    eventBus.emit('errorLlamada', { error: error.message });
    throw error;
  }
}
export async function probarInicializacion() {
  try {
    console.log('🧪 Iniciando prueba de inicialización...');
    
    // Crear device con token hardcodeado para pruebas
    const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCIsImN0eSI6InR3aWxpby1mcGE7dj0xIn0.eyJqdGkiOiJTSzk5YTE5ZDA0MGI1N2E1MmVkNTk4NGJiZjkzM2FkYjVkLTE3NTI3OTczNTIiLCJncmFudHMiOnsiaWRlbnRpdHkiOiJ1c3VhcmlvLXBydWViYSIsInZvaWNlIjp7ImluY29taW5nIjp7ImFsbG93Ijp0cnVlfSwib3V0Z29pbmciOnsiYXBwbGljYXRpb25fc2lkIjoiQVA3ZTkwMmQyZTk5MDBjYjA1MjZlMTdiYjAzZTgwZGM0YyJ9fX0sImlhdCI6MTc1Mjc5NzM1MiwiZXhwIjoxNzUyODAwOTUyLCJpc3MiOiJTSzk5YTE5ZDA0MGI1N2E1MmVkNTk4NGJiZjkzM2FkYjVkIiwic3ViIjoiQUM3ZWQ4YmNlMDgyZjI4ZjdkZWQ2NzMzZWI5NGUwMDYyNiJ9.IKDEHH6ucjGutuixtxqMllW3mRU66pjSNd8LPwOtlS0';
    
    if (device) {
      await limpiarDevice();
    }
    
    device = new Device(token, {
      debug: true,
      allowIncomingWhileBusy: false,
      codecPreferences: ['opus', 'pcmu'],
      identity: 'usuario-prueba'
    });

    console.log('✅ Device creado para prueba:', device);
    console.log('📊 Estado inicial:', device.state);

    // Configurar eventos básicos
    device.on('ready', () => console.log('✅ Device listo'));
    device.on('registered', () => console.log('✅ Device registrado'));
    device.on('error', (error) => console.error('❌ Error:', error.message));
    device.on('offline', () => console.warn('📴 Device offline'));

    // Registrar el device
    console.log('📡 Registrando device...');
    await device.register();
    console.log('✅ Registro completado');
    
    // Monitor del estado por 10 segundos
    const interval = setInterval(() => {
      const estado = obtenerEstado();
      console.log('📊 Estado actual:', estado);
    }, 1000);
    
    setTimeout(() => {
      clearInterval(interval);
      console.log('✅ Prueba completada');
    }, 10000);
    
    return device;
    
  } catch (error) {
    console.error('❌ Error en prueba:', error);
    throw error;
  }
}